import React, { createContext, useContext, useState, useEffect, useCallback, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

import LandingPage from "@/pages/LandingPage";
import ResetPassword from "@/pages/ResetPassword";
import OnboardingModal from "@/components/OnboardingModal";
import AppLayout from "@/components/AppLayout";
import Paywall from "@/pages/Paywall";
import { supabase } from "@/supabaseClient";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const TradeJournal = lazy(() => import("@/pages/TradeJournal"));
const TradeHistory = lazy(() => import("@/pages/TradeHistory"));
const Insights = lazy(() => import("@/pages/Insights"));
const Settings = lazy(() => import("@/pages/Settings"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const PendingApproval = lazy(() => import("@/pages/PendingApproval"));
const PositionCalculator = lazy(() => import("@/pages/PositionCalculator"));
const Thread = lazy(() => import("@/pages/Thread"));
const PaperTrade = lazy(() => import("@/pages/PaperTrade"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);
// Theme Context
const ThemeContext = createContext(null);

export const useAuth = () => useContext(AuthContext);
export const useTheme = () => useContext(ThemeContext);
export { API, BACKEND_URL };

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await supabase.auth.signOut();
    } catch {}
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children, allowPending }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="text-center">
          <div className="w-6 h-6 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-zinc-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
}

function AppWrapper({ children }) {
  const { user } = useAuth();
  const [subStatus, setSubStatus] = useState(null);
  const [subLoading, setSubLoading] = useState(true);

  const checkSubscription = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setSubStatus(res.data);
    } catch { setSubStatus({ active: true }); }
    finally { setSubLoading(false); }
  }, []);

  useEffect(() => {
    if (user) checkSubscription();
  }, [user, checkSubscription]);

  if (subLoading) return null;

  // Show paywall if subscription is not active
  if (subStatus && !subStatus.active) {
    return <Paywall subscription={subStatus.subscription} onSuccess={checkSubscription} />;
  }

  return (
    <>
      {user && !user.onboarded && <OnboardingModal />}
      {children}
    </>
  );
}

function AppRouter() {
  return (
    <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="w-6 h-6 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" /></div>}>
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/pending-approval" element={<ProtectedRoute allowPending><PendingApproval /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><AppLayout><AppWrapper><Dashboard /></AppWrapper></AppLayout></ProtectedRoute>} />
      <Route path="/journal" element={<ProtectedRoute><AppLayout><AppWrapper><TradeJournal /></AppWrapper></AppLayout></ProtectedRoute>} />
      <Route path="/history" element={<ProtectedRoute><AppLayout><AppWrapper><TradeHistory /></AppWrapper></AppLayout></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><AppLayout><AppWrapper><Insights /></AppWrapper></AppLayout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><AppLayout><AppWrapper><Settings /></AppWrapper></AppLayout></ProtectedRoute>} />
      <Route path="/calculator" element={<ProtectedRoute><AppLayout><AppWrapper><PositionCalculator /></AppWrapper></AppLayout></ProtectedRoute>} />
      <Route path="/thread" element={<ProtectedRoute><AppLayout><AppWrapper><Thread /></AppWrapper></AppLayout></ProtectedRoute>} />
      <Route path="/paper-trade" element={<ProtectedRoute><AppLayout><AppWrapper><PaperTrade /></AppWrapper></AppLayout></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AppLayout><AppWrapper><AdminPage /></AppWrapper></AppLayout></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem("mj-theme") || "dark");

  useEffect(() => {
    document.documentElement.classList.toggle("light-theme", theme === "light");
    localStorage.setItem("mj-theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  return (
    <BrowserRouter>
      <ThemeContext.Provider value={{ theme, toggleTheme }}>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" theme={theme} />
        </AuthProvider>
      </ThemeContext.Provider>
    </BrowserRouter>
  );
}

export default App;
